import type { UseQueryResult } from "react-query"
import { useQuery } from "react-query"

import type { AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import type { PaginatedFuzzyMatchList } from "../../../types/generated/api/PaginatedFuzzyMatchList"

/**
 * Custom hook for fetching fuzzy match results for a project and optional prediction.
 * Returns paginated fuzzy match records with optional filtering capabilities.
 *
 * @param params - Configuration object for the fuzzy match query
 * @param params.projectId - The UUID of the project
 * @param params.prediction_uuid - Optional UUID of a specific prediction to filter by
 * @param params.page - Current page number for pagination
 * @param params.pageSize - Number of records per page
 * @param params.filters - Optional filters for fuzzy match results
 * @param params.filters.prediction_uuid - Filter by specific prediction UUID
 * @param params.filters.component_uuid - Filter by specific component UUID
 * @param params.filters.is_fuzzy_successful - Filter by fuzzy match success status
 * @param params.filters.is_matched - Filter by match status
 * @param params.filters.search - Text search filter
 * @returns Query result containing paginated fuzzy match data
 */
export const useFuzzyMatchResults = ({
  projectId,
  prediction_uuid,
  page,
  pageSize,
  filters,
}: {
  projectId: string
  prediction_uuid?: string
  page: number
  pageSize: number
  filters?: {
    prediction_uuid?: string
    component_uuid?: string
    is_fuzzy_successful?: boolean
    is_matched?: boolean
    search?: string
  }
}): UseQueryResult<AxiosResponse<PaginatedFuzzyMatchList>> =>
  useQuery<AxiosResponse<PaginatedFuzzyMatchList>>({
    queryKey: ["fuzzy-match-records", projectId, prediction_uuid, page, pageSize, filters],
    queryFn: () =>
      KonanAPI.getFuzzyMatchResults({
        projectId,
        params: { ...filters, page, page_size: pageSize, prediction_uuid },
      }),
    keepPreviousData: true,
  })
